import React from 'react';
import Box from '@Atoms/Box';
import Typography from '@Atoms/Typography';
import IconButton from '@Atoms/IconButton';
import DateDisplay from '@Atoms/DateDisplay';
import TooltipDisabledButton from '@components/Generals/TooltipDisabledButton';
import { useCurrentSection } from '@reducers/sections/hooks';
import EditIcon from '@mui/icons-material/Edit';
import { TestletStatus, ILEExamSession, MappedTestSession } from '../../types';

type Props = {
  testSession: MappedTestSession;
  setSelectedTestSession: (testSession: ILEExamSession) => void;
};

const ModifyTestSessionCard = ({ testSession, setSelectedTestSession }: Props) => {
  const section = useCurrentSection();
  const disabledEdit = React.useMemo<{ title: string; disabled: boolean } | null>(() => {
    if (section.third_party_id) {
      return {
        title: 'Ask admin to modify test session',
        disabled: true,
      };
    }
    if (
      testSession.testlets.some((testlet) => testlet.status !== TestletStatus.NOT_STARTED)
    ) {
      return {
        title: 'The test is in progress or completed',
        disabled: true,
      };
    }
    if (testSession.policy_enforced) {
      return {
        title: 'Test dates are locked. Contact your administrator.',
        disabled: true,
      };
    }
    return null;
  }, [testSession, section]);

  return (
    <Box
      sx={{
        border: '1px dashed #dcdcdc',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: '#ffffff',
        position: 'relative',
        cursor: 'default',
      }}
    >
      {/* Edit Icon positioned on top left */}
      <Box
        sx={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 1,
        }}
      >
        <TooltipDisabledButton
          disabled={!disabledEdit?.disabled}
          title={disabledEdit?.title}
        >
          <IconButton
            onClick={() => setSelectedTestSession(testSession)}
            disabled={disabledEdit?.disabled}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            <EditIcon
              sx={{
                color: disabledEdit?.disabled ? '#bdbdbd' : 'primary.main',
                fontSize: '18px',
              }}
            />
          </IconButton>
        </TooltipDisabledButton>
      </Box>

      {/* Content with left padding to accommodate the icon */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.2rem',
            color: 'primary.main',
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {testSession.examName}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#939393',
            backgroundColor: '#f3f3f3',
            maxWidth: 'fit-content',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {testSession.examType}
        </Typography>

        <DateDisplay
          startDate={testSession.start_date}
          endDate={testSession.end_date}
          variant="default"
          size="medium"
          showIcon
        />
      </Box>
    </Box>
  );
};

export default ModifyTestSessionCard;
